import { useCallback } from "react"

import { useDropzone } from "react-dropzone"
import { useTranslation } from "react-i18next"

import type { LocalFileUploadProps } from "../types"

import { CircleX, CloudUpload, Upload } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { formatBytes, getReadableFileType, renderFileIcon } from "../utils"

const removeButtonStyles =
  "flex size-5 cursor-pointer items-center justify-center rounded-full bg-card text-card-foreground ring-1 ring-border shadow-sm transition-[color,background-color,box-shadow,scale] duration-150 hover:scale-110 hover:bg-destructive/10 hover:text-destructive hover:shadow-md active:scale-95 sm:size-6"

export function LocalFileUpload({
  files,
  onAddFiles,
  onRemoveFile,
  acceptOnlyImages = false,
  accept,
  formatsHint,
  onRejectFiles,
}: LocalFileUploadProps) {
  const { t } = useTranslation()

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length) {
        onAddFiles(acceptedFiles)
      }
    },
    [onAddFiles]
  )

  const imageTypes = {
    "image/*": [".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp", ".svg"],
  }

  const allTypes = {
    // Keep CSV explicit. Chromium on macOS disables .csv rows in the native
    // picker when the extension is grouped only under text/*, even though the
    // generated accept attribute also contains .csv. text/csv makes the file
    // selectable while the extension remains the cross-platform fallback.
    "text/*": [".txt", ".md", ".json", ".xml", ".html", ".rtf", ".prn"],
    "text/csv": [".csv"],
    "application/pdf": [".pdf"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.ms-powerpoint": [".ppt"],
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
    "image/*": [".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp", ".svg"],
  }

  // Use custom accept if provided, otherwise fall back to acceptOnlyImages logic
  const acceptedFileTypes = accept ?? (acceptOnlyImages ? imageTypes : allTypes)

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    multiple: true,
    accept: acceptedFileTypes,
    // Without this a refused file (an image picked for a hosted agent) just
    // vanished: react-dropzone hands `onDrop` an empty list and nothing says why.
    onDropRejected: onRejectFiles,
  })

  const getMainTitle = () => {
    if (isDragActive) {
      if (isDragAccept) {
        return t("filepicker.drop_files_here", { defaultValue: "Drop files here" })
      }
      return t("filepicker.unsupported_file_type", { defaultValue: "Some files are not supported" })
    }
    if (files.length === 0) {
      return t("filepicker.drag_drop_or_browse", { defaultValue: "Drop files here or browse" })
    }
    return t("filepicker.files_ready_to_add", { defaultValue: "Files ready to add" })
  }

  const getSubtitle = () => {
    if (isDragActive) {
      if (isDragAccept) {
        return t("filepicker.release_to_upload", { defaultValue: "Release to upload files" })
      }
      return t("filepicker.only_supported_files", {
        defaultValue: "Only supported file types allowed",
      })
    }
    if (files.length === 0) {
      if (formatsHint) return formatsHint
      return t("filepicker.supported_formats", {
        defaultValue: acceptOnlyImages
          ? "Supports images only"
          : "Supports documents, images, and text files",
      })
    }
    return t("filepicker.files_selected", {
      defaultValue: "{{count}} file(s) selected",
      count: files.length,
    })
  }

  return (
    <div className="flex h-full flex-col gap-3 px-1 sm:gap-6 sm:px-0">
      {/* Modern File Upload Area with Drag & Drop */}
      <div className="flex-shrink-0">
        <div
          {...getRootProps()}
          className={cn(
            "group cursor-pointer rounded-xl border-2 border-dashed p-3 text-center transition-colors duration-200 sm:p-8",
            {
              "border-muted-foreground/25 bg-muted/20 hover:border-muted-foreground/40 hover:bg-muted/30":
                !isDragActive,
              "border-primary/60 bg-primary/10 hover:border-primary/70 hover:bg-primary/15":
                isDragActive && isDragAccept,
              "border-destructive/60 bg-destructive/10 hover:border-destructive/70 hover:bg-destructive/15":
                isDragActive && isDragReject,
            }
          )}
        >
          <input
            {...getInputProps()}
            aria-label={t("filepicker.upload_local_files_aria", {
              defaultValue: "Upload local files",
            })}
          />
          <div className="flex flex-col items-center space-y-2 sm:space-y-4">
            <div
              className={cn("rounded-full p-1.5 transition-colors duration-200 sm:p-3", {
                "bg-primary/10 group-hover:bg-primary/15": !isDragActive,
                "bg-primary/20": isDragActive && isDragAccept,
                "bg-destructive/20": isDragActive && isDragReject,
              })}
            >
              <CloudUpload
                className={cn("h-5 w-5 transition-colors duration-200 sm:h-8 sm:w-8", {
                  "text-primary": !isDragActive || isDragAccept,
                  "text-destructive": isDragActive && isDragReject,
                })}
              />
            </div>
            <div className="space-y-1 sm:space-y-2">
              <h3 className="text-foreground text-sm font-semibold sm:text-lg">{getMainTitle()}</h3>
              <p className="text-muted-foreground text-xs sm:text-sm">{getSubtitle()}</p>
            </div>
            <div className="flex h-8 items-center justify-center sm:h-10">
              {files.length === 0 && !isDragActive && (
                <Button
                  className="h-7 px-3 text-xs sm:h-10 sm:px-4 sm:text-sm"
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  <Upload className="mr-1 h-3 w-3 sm:mr-2 sm:h-4 sm:w-4" />
                  {t("filepicker.choose_files", { defaultValue: "Choose Files" })}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Always present file list area */}
      <div className="flex min-h-0 flex-1 flex-col">
        {files.length > 0 && (
          <div className="flex-shrink-0 pb-2 sm:pb-3">
            <h4 className="text-foreground text-sm font-medium sm:text-base">
              {t("filepicker.selected_files_count", {
                defaultValue: "Selected Files ({{count}})",
                count: files.length,
              })}
            </h4>
          </div>
        )}
        <div className="flex-1 overflow-auto">
          <div className="space-y-1.5 sm:space-y-3">
            {files.length > 0 ? (
              files.map((file, idx) => (
                <div
                  key={`${file.name}-${idx}`}
                  className="group bg-card hover:bg-accent/50 flex items-center gap-2 rounded-lg border p-2 transition-colors sm:gap-3 sm:p-4"
                >
                  <div className="flex-shrink-0">{renderFileIcon(file.name, file.type, false)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-foreground truncate text-xs font-medium sm:text-sm">
                      {file.name}
                    </div>
                    <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-[10px] sm:mt-1 sm:gap-2 sm:text-xs">
                      <span>{getReadableFileType(file.name, file.type)}</span>
                      <span>•</span>
                      <span>{formatBytes(file.size)}</span>
                    </div>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className={removeButtonStyles}
                        type="button"
                        onClick={() => onRemoveFile(file)}
                      >
                        <CircleX className="size-3 sm:size-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t("filepicker.remove_file", { defaultValue: "Remove file" })}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              ))
            ) : (
              <div className="flex h-full items-center justify-center py-6 sm:py-12">
                <p className="text-muted-foreground text-xs sm:text-sm">
                  {t("filepicker.selected_files_placeholder", {
                    defaultValue: "Selected files will appear here",
                  })}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
